/**
 * Help Center (docs.valiyou.com) article index for the in-app AI assistant.
 *
 * Mintlify serves every page as raw markdown at {path}.md and the full index
 * at /llms.txt. The static list below mirrors the site's menu structure
 * (snapshot 2026-10-09) and acts as the whitelist for get_help_article.
 * Regenerate from https://docs.valiyou.com/llms.txt when the docs change.
 */

export const HELP_CENTER_BASE_URL = 'https://docs.valiyou.com';

export interface HelpArticle {
  /** Path without leading slash and without .md, e.g. 'getting-started/quick-start'. */
  path: string;
  /** Menu title as shown on docs.valiyou.com. */
  title: string;
  /** Short description (truncated from llms.txt). */
  description: string;
}

export const HELP_ARTICLES: HelpArticle[] = [
  // Get started
  {
    path: 'index',
    title: 'Knowledge Center',
    description: 'Complete guide to sponsorship valuation, package building and reporting.',
  },
  {
    path: 'getting-started/quick-start',
    title: 'Quick Start',
    description:
      'Onboarding: account creation, organization setup, plan selection, first valuation.',
  },
  {
    path: 'getting-started/your-profile',
    title: 'Your Profile',
    description: 'Avatar, personal info, language, password management.',
  },
  {
    path: 'getting-started/organization-settings',
    title: 'Organization',
    description: 'Logo for branded reports/proposals, base currency, platform settings.',
  },
  {
    path: 'getting-started/invite-team-members',
    title: 'Invite Team',
    description: 'Invite team members, role-based access, managing invitations.',
  },
  // Valuations
  {
    path: 'valuations/create-valuation',
    title: 'Create Valuation',
    description: 'CPM methodology: sales revenue, transfers, TV, social media metrics.',
  },
  {
    path: 'valuations/dashboard',
    title: 'Valuations Dashboard',
    description: 'Visual analytics, trend analysis, KPIs across sales, media and brand.',
  },
  {
    path: 'valuations/valuations-list',
    title: 'Valuations List',
    description: 'Browse, filter, compare and analyze value changes over time.',
  },
  {
    path: 'valuations/valuation-details',
    title: 'Valuation Details',
    description: 'Full data breakdown, metric analysis, detailed calculations.',
  },
  {
    path: 'valuations/reports',
    title: 'Reports',
    description: 'PDF exports, stakeholder presentations, comparison with trend analysis.',
  },
  {
    path: 'valuations/methods',
    title: 'Methods',
    description: 'Valuation methodology: CPM-based valuation, brand multipliers.',
  },
  // Sponsorships
  {
    path: 'sponsorships/sponsors',
    title: 'Sponsors',
    description: 'Sponsor relationships, contacts, values, statuses, portfolios.',
  },
  {
    path: 'sponsorships/packages',
    title: 'Packages',
    description: 'Package Builder: inventory items, pricing, proposal preview, offers.',
  },
  {
    path: 'sponsorships/inventory/inventory',
    title: 'Inventory',
    description: 'Quantity tracking, transaction history, reordering, overselling prevention.',
  },
  {
    path: 'sponsorships/inventory/settings',
    title: 'Inventory Settings',
    description: 'Custom categories, tier hierarchies, EVENT management, custom fields.',
  },
  {
    path: 'sponsorships/inventory/negotiator',
    title: 'Negotiator Mode',
    description: 'Cost-plus pricing, margin calculations, coverage percentage labels.',
  },
  {
    path: 'sponsorships/proposals',
    title: 'Proposals',
    description: 'Send proposals, track approval status, branded PDFs, digital signatures.',
  },
  {
    path: 'sponsorships/analytics',
    title: 'Sponsorship Analytics',
    description: 'Revenue, package performance, sponsor engagement, ROI, KPIs.',
  },
  // Platform
  {
    path: 'platform/team-management',
    title: 'Team',
    description: 'Invite users, roles, seats, granular access control.',
  },
  {
    path: 'platform/company',
    title: 'Organization (Platform)',
    description: 'Logo, company details, address, CVR/VAT numbers.',
  },
  {
    path: 'platform/settings/general',
    title: 'Settings - General',
    description: 'Logo, company details, base currency, brand value indicators.',
  },
  {
    path: 'platform/settings/brand-values',
    title: 'Settings - Valuations',
    description: 'Default brand value indicators: popularity, CSR, culture, loyalty.',
  },
  {
    path: 'platform/settings/negotiator',
    title: 'Settings - Sponsorships',
    description: 'Custom negotiator labels, contribution margin indicators.',
  },
  {
    path: 'guides/valuations/cpm-benchmarks',
    title: 'Settings - CPM Benchmarks',
    description: '81+ CPM benchmarks for social platforms across 12 countries.',
  },
  {
    path: 'guides/valuations/currency-conversion',
    title: 'Settings - Currency Conversion',
    description: 'Automatic currency conversion, daily exchange rates.',
  },
  {
    path: 'platform/settings/sso',
    title: 'SSO',
    description: 'Enterprise SSO: SAML 2.0, OAuth 2.0, OIDC (Okta, Azure AD, Google).',
  },
  {
    path: 'platform/settings/documents',
    title: 'Documents',
    description: 'Contract documents, Terms of Service, branded PDF covers.',
  },
  {
    path: 'platform/billing/payment-methods',
    title: 'Payment Methods',
    description: 'Manage cards securely via Stripe.',
  },
  {
    path: 'platform/billing/invoices',
    title: 'Invoices',
    description: 'Download PDF invoices, invoice history, payment status.',
  },
  {
    path: 'platform/billing/billing-details',
    title: 'Billing Details',
    description: 'Company billing info, address, VAT number, tax details.',
  },
  {
    path: 'platform/plugins',
    title: 'Plugins',
    description: 'AI insights, Universal CSV Importer, Sankey cash flow, SocialDeepDive.',
  },
  {
    path: 'platform/plugins/universal-importer',
    title: 'Universal Importer',
    description: 'Import sponsors/contacts from Salesforce, HubSpot, Pipedrive (CSV).',
  },
  {
    path: 'platform/plugins/ai-insights',
    title: 'AI Insights',
    description: 'AI-powered insights from valuations (Groq, OpenAI, Gemini, Claude).',
  },
  {
    path: 'platform/plugins/cash-flow-visualization',
    title: 'Cash Flow',
    description: 'Sankey diagrams: revenue streams, payment schedules, forecasting.',
  },
  {
    path: 'platform/plugins/social-deep-dive',
    title: 'Social Analytics',
    description: 'Social media analytics across 12+ platforms.',
  },
  {
    path: 'platform/api',
    title: 'API',
    description: 'API keys, tokens, usage monitoring, Power BI/Excel integration.',
  },
  {
    path: 'platform/mcp',
    title: 'AI Assistants (MCP)',
    description: 'Connect Claude/ChatGPT to Valiyou via MCP.',
  },
  {
    path: 'platform/user-profile',
    title: 'Profile',
    description: 'Profile picture, password, language, security, account deletion.',
  },
  // Authentication
  {
    path: 'authentication/login',
    title: 'Login',
    description: 'Email, password or social login; SSO and demo mode.',
  },
  {
    path: 'authentication/signup',
    title: 'Sign Up',
    description: 'Create an account with email or social sign-up.',
  },
  {
    path: 'authentication/verify-email',
    title: 'Verify Email',
    description: 'Email confirmation and account activation.',
  },
  {
    path: 'authentication/forgot-password',
    title: 'Forgot Password',
    description: 'Request a password reset link.',
  },
  {
    path: 'authentication/reset-password',
    title: 'Reset Password',
    description: 'Set a new password securely.',
  },
  // Trust & Security
  {
    path: 'trust-security/trust-center',
    title: 'Trust Center',
    description: 'ISO 27001, GDPR, SOC 2 Type II, encryption, compliance.',
  },
  // Guides
  {
    path: 'guides/sponsor-deal-workflow',
    title: 'Close Your First Sponsorship Deal',
    description: 'End-to-end: sponsor profile → proposal → approval tracking.',
  },
  {
    path: 'guides/quarterly-reporting',
    title: 'Quarterly Sponsor Reports',
    description: 'Quarterly reports with comparison analytics and AI insights.',
  },
  {
    path: 'guides/api-powerbi-integration',
    title: 'Connect to Power BI with API',
    description: 'Automated dashboards, real-time metrics, scheduled refresh.',
  },
  // Resources
  {
    path: 'resources/glossary',
    title: 'Glossary',
    description: 'Sponsorship terminology: CPM, brand exposure, engagement rate…',
  },
];

const HELP_ARTICLE_PATHS = new Set(HELP_ARTICLES.map((a) => a.path));

export function isHelpArticlePath(path: string): boolean {
  return HELP_ARTICLE_PATHS.has(path);
}

/** One line per article for the system prompt (kept compact — it is cached). */
export const HELP_ARTICLES_PROMPT_LIST = HELP_ARTICLES.map(
  (a) => `- ${a.path} — ${a.title}: ${a.description}`
).join('\n');

/** Max characters of article markdown returned to the model. */
const MAX_ARTICLE_CHARS = 8000;
/** In-memory cache TTL — docs change rarely, serverless instances are short-lived. */
const CACHE_TTL_MS = 15 * 60 * 1000;

const articleCache = new Map<string, { content: string; fetchedAt: number }>();

/**
 * Fetch a help article as raw markdown from docs.valiyou.com (server-side).
 * Returns null when the article cannot be fetched. Path must be whitelisted
 * via isHelpArticlePath before calling.
 */
export async function fetchHelpArticle(path: string): Promise<string | null> {
  const cached = articleCache.get(path);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.content;
  }
  try {
    const res = await fetch(`${HELP_CENTER_BASE_URL}/${path}.md`, {
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;
    const text = await res.text();
    const content = text.slice(0, MAX_ARTICLE_CHARS);
    articleCache.set(path, { content, fetchedAt: Date.now() });
    return content;
  } catch {
    return null;
  }
}
